import Vue from 'vue';
import Vuex from 'vuex';

const uuidv1 = require('uuid/v1');

Vue.use(Vuex);

function defaultState() {
  return {
    config: {
      url: process.env.VUE_APP_REDIS_HOST ? process.env.VUE_APP_REDIS_HOST : '/api/v1/public',
      tierType: '',
      isTIER3SpecialEvent: false,
    },
    socket: null,
    id: null,
    isConnected: false,
    userRegistered: false,
    questions: [],
    currentQuestionIndex: -1,
    currentQuestion: null,
    currentQuestionCount: null,
    remainingTimeInSeconds: null,
    remainingQuestionTimeInSeconds: null,
    correctAnswer: '',
    selectedAnswer: '',
    showAnswer: false,
    sessionIndex: 0,
    gameStarted: false,
    userReady: false,
    countdownInterval: null,
    registerCountdownInterval: null,
    showPage: false,
    activeModule: 'Attract',
    questionTimerComplete: false,
    questionStartTime: null,
    firstName: '',
    lastName: '',
    email: '',
    zip: '',
    optIn: false,
    lead: false,
    claimCode: '',
    triggerDisconnect: false,
    triggerConnect: false,
    tier1UserId: '',
    eventId: -1, // 50009057
    eventName: '',
    gameRoomId: -1,
    invalidGameRoomId: false,
    singleUserScore: 0,
  };
}

export default new Vuex.Store({
  state: defaultState,
  getters: {
    socket: state => state.socket,
    id: state => state.id,
    isConnected: state => state.isConnected,
    userRegistered: state => state.userRegistered,
    questions: state => state.questions,
    currentQuestionIndex: state => state.currentQuestionIndex,
    correctAnswer: state => state.correctAnswer,
    selectedAnswer: state => state.selectedAnswer,
    showAnswer: state => state.showAnswer,
    sessionIndex: state => state.sessionIndex,
    gameStarted: state => state.gameStarted,
    userReady: state => state.userReady,
    countdownInterval: state => state.countdownInterval,
    registerCountdownInterval: state => state.registerCountdownInterval,
    showPage: state => state.showPage,
    activeModule: state => state.activeModule,
    questionTimerComplete: state => state.questionTimerComplete,
    questionStartTime: state => state.questionStartTime,
    firstName: state => state.firstName,
    lastName: state => state.lastName,
    email: state => state.email,
    zip: state => state.zip,
    optIn: state => state.optIn,
    lead: state => state.lead,
    config: state => state.config,
    currentQuestion: state => state.currentQuestion,
    currentQuestionCount: state => state.currentQuestionCount,
    remainingTimeInSeconds: state => state.remainingTimeInSeconds,
    remainingQuestionTimeInSeconds: state => state.remainingQuestionTimeInSeconds,
    claimCode: state => state.claimCode,
    triggerDisconnect: state => state.triggerDisconnect,
    triggerConnect: state => state.triggerConnect,
    tier1UserId: state => state.tier1UserId,
    gameRoomId: state => state.gameRoomId,
    eventId: state => state.eventId,
    eventName: state => state.eventName,
    invalidGameRoomId: state => state.invalidGameRoomId,
    singleUserScore: state => state.singleUserScore,
  },
  /* eslint-disable no-param-reassign */
  mutations: {
    setGameRoomId(state, payload) {
      state.gameRoomId = payload.gameRoomId;
    },
    setEventId(state, payload) {
      state.eventId = payload.value;
    },
    setEventName(state, payload) {
      state.eventName = payload;
    },
    setInvalidGameRoomId(state, payload) {
      state.invalidGameRoomId = payload.value;
    },
    setSocket(state, payload) {
      state.socket = payload.value;
    },
    setId(state, payload) {
      state.id = payload.value;
    },
    setIsConnected(state, payload) {
      state.isConnected = payload.value;
    },
    setUserRegistered(state, payload) {
      state.userRegistered = payload.value;
    },
    setQuestions(state, payload) {
      state.questions = payload.value;

      // Add uuids to indexable items (used for VueJS reactivity)
      state
        .questions
        .children
        .forEach((session) => {
          Vue.set(session, 'uuid', uuidv1());
          session.fields.quiz_repeater.forEach((question) => {
            Vue.set(question, 'uuid', uuidv1());
          });
        });
    },
    setCurrentQuestionIndex(state, payload) {
      state.currentQuestionIndex = payload.value;
    },
    setCorrectAnswer(state, payload) {
      state.correctAnswer = payload.value;
    },
    setSelectedAnswer(state, payload) {
      state.selectedAnswer = payload.value;
    },
    setShowAnswer(state, payload) {
      state.showAnswer = payload.value;
    },
    setSessionIndex(state, payload) {
      state.sessionIndex = payload.value;
    },
    setGameStarted(state, payload) {
      state.gameStarted = payload.value;
    },
    setUserReady(state, payload) {
      state.userReady = payload.value;
    },
    setCountdownInterval(state, payload) {
      state.countdownInterval = payload.value;
    },
    setRegisterCountdownInterval(state, payload) {
      state.registerCountdownInterval = payload.value;
    },
    setShowPage(state, payload) {
      state.showPage = payload.value;
    },
    setActiveModule(state, payload) {
      state.activeModule = payload.value;
    },
    setQuestionTimerComplete(state, payload) {
      state.questionTimerComplete = payload.value;
    },
    setQuestionStartTime(state, payload) {
      state.questionStartTime = payload.value;
    },
    setFirstName(state, payload) {
      state.firstName = payload.value;
    },
    setLastName(state, payload) {
      state.lastName = payload.value;
    },
    setEmail(state, payload) {
      state.email = payload.value;
    },
    setZip(state, payload) {
      state.zip = payload.value;
    },
    setOptIn(state, payload) {
      state.optIn = payload.value;
    },
    setLead(state, payload) {
      state.lead = payload.value;
    },
    setCurrentQuestion(state, payload) {
      state.currentQuestion = payload.value;
    },
    setCurrentQuestionCount(state, payload) {
      state.currentQuestionCount = payload.value;
    },
    setRemainingTimeInSeconds(state, payload) {
      state.remainingTimeInSeconds = payload.value;
    },
    setRemainingQuestionTimeInSeconds(state, payload) {
      state.remainingQuestionTimeInSeconds = payload.value;
    },
    setClaimCode(state, payload) {
      state.claimCode = payload.value;
    },
    setTriggerDisconnect(state, payload) {
      state.triggerDisconnect = payload.value;
    },
    setTriggerConnect(state, payload) {
      state.triggerConnect = payload.value;
    },
    reset(state) {
      const s = defaultState();
      Object.keys(s).forEach((key) => {
        if (key !== 'activeModule'
          && key !== 'config'
          && key !== 'claimCode'
        ) {
          state[key] = s[key];
        }
      });
    },
    setQuestion(state, payload) {
      state
        .questions
        .children[payload.sessionIndex]
        .fields
        .quiz_repeater[payload.questionIndex]
        .fields = {
          quiz_question: payload.question.quiz_question,
          quiz_answer_a: payload.question.quiz_answer_a,
          quiz_answer_b: payload.question.quiz_answer_b,
          quiz_answer_c: payload.question.quiz_answer_c,
          quiz_correct_answer: payload.question.quiz_correct_answer,
        };
    },
    removeQuestion(state, payload) {
      state
        .questions
        .children[payload.sessionIndex]
        .fields
        .quiz_repeater
        .splice(payload.questionIndex, 1);
    },
    renameQuestions(state, payload) {
      // Re-index Questions indexes
      state
        .questions
        .children[payload.sessionIndex]
        .fields
        .quiz_repeater
        .forEach((question, index) => {
          question.name = `question-${index + 1}`;
        });
    },
    setTierType(state, payload) {
      state
        .config
        .tierType = payload.tierType;
    },
    setTier1UserId(state, payload) {
      state.tier1UserId = payload.tier1UserId;
    },
    setSingleUserScore(state, payload) {
      state.singleUserScore = payload.value;
    },
    setIsTIER3SpecialEvent(state, payload) {
      state
        .config
        .isTIER3SpecialEvent = payload.isTIER3SpecialEvent;
    },
  },
  /* eslint-enable no-param-reassign */
});
